import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { Bomb, Diamond } from "lucide-react";
import { toast } from "sonner";

export default function Mines() {
  const { user, currency, refresh } = useApp();
  const [searchParams] = useSearchParams();
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [minesCount, setMinesCount] = useState(() => {
    const p = searchParams.get("mines");
    return p ? Math.min(24, Math.max(1, Number(p))) : 3;
  });
  const [round, setRound] = useState(null); // {round_id, revealed, multiplier, state}
  const [minePositions, setMinePositions] = useState([]);
  const [payout, setPayout] = useState(0);
  const [nextMult, setNextMult] = useState(1.0);
  const [busy, setBusy] = useState(false);

  const startGame = async () => {
    setBusy(true);
    try {
      const r = await api.post("/instant/mines/start", {
        bet: Number(bet), currency, mines: Number(minesCount),
      });
      setRound({ ...r.data, state: "running" });
      setMinePositions([]);
      setPayout(0);
      setNextMult(1.0);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reveal = async (tile) => {
    if (!round || round.state !== "running" || busy) return;
    if (round.revealed.includes(tile)) return;
    setBusy(true);
    try {
      const r = await api.post("/instant/mines/reveal", { round_id: round.round_id, tile });
      if (r.data.state === "lost") {
        setRound({ ...round, revealed: r.data.revealed, state: "lost" });
        setMinePositions(r.data.mine_positions);
        toast.error("💣 BOOM! You hit a mine.");
        refresh();
      } else if (r.data.state === "cashed") {
        setRound({ ...round, revealed: r.data.revealed, state: "cashed" });
        setMinePositions(r.data.mine_positions);
        setPayout(r.data.payout);
        toast.success(`Full board! Won ${fmt(r.data.payout, currency)}`);
        refresh();
      } else {
        setRound({ ...round, revealed: r.data.revealed, multiplier: r.data.multiplier });
        setNextMult(r.data.next_multiplier);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const cashout = async () => {
    if (!round || round.state !== "running") return;
    setBusy(true);
    try {
      const r = await api.post("/instant/mines/cashout", { round_id: round.round_id });
      setRound({ ...round, state: "cashed" });
      setMinePositions(r.data.mine_positions);
      setPayout(r.data.payout);
      toast.success(`Cashed ${r.data.multiplier}x → ${fmt(r.data.payout, currency)}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const isRunning = round?.state === "running";
  const isEnded = round?.state === "lost" || round?.state === "cashed";

  const currentMult = round?.multiplier ?? 1.0;

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Bomb className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Mines</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">5×5 grid · pick safe tiles</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="lux-card p-6">
          <div className="grid grid-cols-5 gap-3 max-w-[560px] mx-auto">
            {Array.from({ length: 25 }).map((_, i) => {
              const revealed = round?.revealed?.includes(i);
              const isMine = minePositions.includes(i);
              const showMines = isEnded && isMine;
              return (
                <button
                  key={i}
                  data-testid={`mines-tile-${i}`}
                  disabled={!isRunning || busy || revealed}
                  onClick={()=>reveal(i)}
                  className={`aspect-square rounded-lg border transition transform ${
                    revealed && !isMine ? "bg-emerald-900/60 border-emerald-400/60 scale-95" :
                    showMines ? "bg-red-900/60 border-red-500" :
                    isRunning ? "bg-[#1a1a1a] border-white/20 hover:border-[#d4af37]/60 hover:scale-105" :
                    "bg-[#111] border-white/10 opacity-60"
                  } flex items-center justify-center text-3xl`}>
                  {revealed && !isMine && <Diamond className="text-emerald-300" size={28}/>}
                  {showMines && "💣"}
                </button>
              );
            })}
          </div>
          {isRunning && (
            <div className="mt-6 text-center">
              <div className="text-white/50 text-xs uppercase tracking-widest">Current Multiplier</div>
              <div className="font-serif text-4xl text-[#d4af37]">{currentMult.toFixed(2)}x</div>
              <div className="text-xs text-white/40 mt-1">Next: {nextMult.toFixed(2)}x</div>
            </div>
          )}
          {round?.state === "cashed" && (
            <div className="mt-6 text-center">
              <div className="text-emerald-400 font-bold">💎 Won {fmt(payout, currency)} at {currentMult.toFixed(2)}x</div>
            </div>
          )}
          {round?.state === "lost" && (
            <div className="mt-6 text-center">
              <div className="text-red-500 font-bold">💥 Hit a mine — lost {fmt(round.bet ?? bet, currency)}</div>
            </div>
          )}
        </div>

        <div className="lux-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Bet</label>
            <input data-testid="mines-bet" type="number" value={bet} onChange={e=>setBet(e.target.value)} disabled={isRunning}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 font-serif text-xl focus:border-[#d4af37]"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Mines: {minesCount}</label>
            <input data-testid="mines-count" type="range" min={1} max={24} value={minesCount}
                   onChange={e=>setMinesCount(Number(e.target.value))} disabled={isRunning}
                   className="w-full"/>
            <div className="text-xs text-white/40 mt-1">More mines = higher rewards but higher risk</div>
          </div>
          <div className="rounded bg-black/40 border border-white/10 p-3 text-xs text-white/50">
            Balance: <span className="text-[#d4af37] font-bold">{fmt(currency==="HUF"?user?.balance_huf:user?.balance_eur, currency)}</span>
          </div>
          {isRunning ? (
            <button data-testid="mines-cashout" onClick={cashout} disabled={busy || round?.revealed?.length === 0}
                    className="w-full btn-gold px-6 py-4 rounded-md text-lg">
              Cash Out · {currentMult.toFixed(2)}x
            </button>
          ) : (
            <button data-testid="mines-start" onClick={startGame} disabled={busy}
                    className="w-full btn-burgundy px-6 py-4 rounded-md text-lg uppercase tracking-widest">
              Start Game
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
